import { useAutoAnimate } from "@formkit/auto-animate/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FeeProfileType, PaymentGateway } from "@prisma/client";
import * as RadioGroup from "@radix-ui/react-radio-group";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import type { RouterOutputs } from "@calcom/trpc/react";
import { trpc } from "@calcom/trpc/react";
import { Button, Label, TextField, Form, showToast, TextAreaField, Switch } from "@calcom/ui";
import { defaultFinixMerchantFeeProfile } from "@calcom/web/lib/finix";

import { AlertIcon, DollarIcon } from "@components/Icons";
import { useOfficeContext } from "@components/navbar";
import { OfficePatientSelect } from "@components/patients/OfficePatientSelect";
import PaymentSuccessDialog from "@components/patients/PaymentSuccessDialog";
import { PatientStoredPaymentMethodSelect } from "@components/payment/PatientStoredPaymentMethodSelect";
import PaymentForm from "@components/payment/PaymentForm";
import PayrixForm from "@components/payment/PayrixForm";

type Props = {
  patientId?: number;
  officeId: number;
  appId: string;
  env: string;
  setPaymentDialog: React.Dispatch<React.SetStateAction<boolean>>;
  payrixApiKey?: string;
  payrixPaymentEnv?: string;
};

type FormValues = {
  cardId?: number;
  amount: number;
  note: string;
  insurancePayer?: string;
  claimId?: number;
  enableSurcharge: boolean;
};

type Card = RouterOutputs["viewer"]["patients"]["getCardList"][number];

export default function InsurancePayment({
  patientId,
  officeId,
  appId,
  env,
  setPaymentDialog,
  payrixApiKey,
  payrixPaymentEnv,
}: Props) {
  const [patient, setPatient] = useState<number | undefined>(patientId);
  const [feeRate, setFeeRate] = useState(defaultFinixMerchantFeeProfile.cnpPercFee);

  const [animationParentRef] = useAutoAnimate<HTMLDivElement>();
  const [loading, setLoading] = useState(false);
  const [paymentOption, setPaymentOption] = useState("add-new");
  const [createPatient, setCreatePatient] = useState(false);
  const [card, setCard] = useState<Card | undefined>(undefined);
  const [isACHPayment, setIsACHPayment] = useState(false);
  const [paymentSuccessDialogOpen, setPaymentSuccessDialogOpen] = useState(false);
  const [processedAmount, setProcessedAmount] = useState(0);
  const [feeCharged, setFeeCharged] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: {
      enableSurcharge: false,
      amount: 0,
      note: "",
    },
    resolver: zodResolver(
      z.object({
        cardId: z.number().optional(),
        amount: z.number().min(0.01),
        insurancePayer: z.string().optional(),
        claimId: z.number().optional(),
        note: z.string().optional(),
        enableSurcharge: z.boolean(),
      })
    ),
  });
  const { register, formState, watch, setValue, trigger, reset } = form;

  const watchAmount = watch("amount");
  const watchNote = watch("note");
  const watchInsurancePayer = watch("insurancePayer");
  const watchClaimId = watch("claimId");
  const watchEnableSurcharge = watch("enableSurcharge");

  const { office } = useOfficeContext();

  const { data: singlePatient } = trpc.viewer.patients.getById.useQuery(
    {
      billingOfficeId: officeId,
      patientId: Number(patient),
      _tag: "insurancePayment",
    },
    {
      enabled: !!patient,
      refetchInterval: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (singlePatient) {
      if (singlePatient.cardList.length) {
        setPaymentOption("use-existing");
      } else {
        setPaymentOption("add-new");
      }
    }
  }, [singlePatient]);

  useEffect(() => {
    if (singlePatient) {
      setValue("amount", singlePatient?.balance || 0);
    }
  }, [singlePatient]);

  useEffect(() => {
    if (office && card) {
      setValue(
        "enableSurcharge",
        (office.applySurchargeFee &&
          office.surchargeOnVirtualPay &&
          !isACHPayment &&
          card.cardType?.toLowerCase() === "credit") ||
          (office.enableDualPricing && !isACHPayment)
      );
    }
  }, [office, isACHPayment, card, setValue]);

  useEffect(() => {
    if (office && office.FinixMerchantFeeProfile) {
      setFeeRate(
        office.FinixMerchantFeeProfile.feeProfileType === FeeProfileType.PAYMENT_TYPE
          ? office.FinixMerchantFeeProfile.cnpPercFee
          : office.FinixMerchantFeeProfile.creditCardPercFee
      );
    }
  }, [office]);

  useEffect(() => {
    if (!office || card) return;
    setValue(
      "enableSurcharge",
      (office.applySurchargeFee && office.surchargeOnVirtualPay) || office.enableDualPricing
    );
  }, [office, card, setValue]);

  useEffect(() => {
    if (card) {
      setIsACHPayment(card.type === "BANK_ACCOUNT");
    }
  }, [card, paymentOption]);

  const saveInsurancePayment = trpc.viewer.patients.processInsurancePayment.useMutation({
    onSuccess: ({ amount, surchargeApplied }) => {
      setProcessedAmount(amount);
      setFeeCharged(surchargeApplied);
      setLoading(false);
      showToast("Success", "success");
      setPaymentSuccessDialogOpen(true);
    },
    onError: (e) => {
      showToast(e.message || "Error", "error");
      setLoading(false);
    },
  });

  const handleSubmit = (data: FormValues) => {
    if (!data.cardId && patient) {
      showToast("Please select a payment method", "error");
      return;
    }
    if (loading) return;
    setLoading(true);
    saveInsurancePayment.mutate({
      patientId: patient || null,
      officeId,
      ...data,
      enableSurcharge: data.enableSurcharge,
      isNewMethod: false,
    });
  };

  const handleFinixPayment = async (token: string) => {
    const validAmount = await trigger("amount");
    if (!validAmount) return;
    setLoading(true);

    saveInsurancePayment.mutate({
      patientId: patient || null,
      officeId,
      amount: watchAmount,
      enableSurcharge: watchEnableSurcharge,
      note: watchNote,
      insurancePayer: watchInsurancePayer,
      claimId: watchClaimId,
      token,
      isNewMethod: true,
    });
  };

  const handlePayrixPayment = async (tokenId: string) => {
    const validAmount = await trigger("amount");
    if (!validAmount) {
      setLoading(false);
      return;
    }
    setLoading(true);

    saveInsurancePayment.mutate({
      patientId: patient || null,
      officeId,
      amount: watchAmount,
      enableSurcharge: watchEnableSurcharge,
      note: watchNote,
      insurancePayer: watchInsurancePayer,
      claimId: watchClaimId,
      tokenId,
      isNewMethod: true,
    });
  };

  return (
    <div className="rounded-md bg-white text-sm text-gray-500">
      <Form form={form} handleSubmit={handleSubmit}>
        <div className="mt-6 flex flex-col gap-5">
          <div id="insurance-payment-account-select">
            <Label>Account (Optional)</Label>
            <div className="relative w-full">
              {createPatient ? (
                <TextField placeholder="Input Patient Name" />
              ) : (
                <OfficePatientSelect
                  officeId={officeId}
                  value={patient}
                  onChange={(id) => {
                    if (id === -1) {
                      setCreatePatient(true);
                    } else {
                      setPatient(id);
                    }
                  }}
                  placeholder="Find Account"
                  className="relative  rounded-xl"
                  showMissingPatientHelp
                />
              )}
            </div>
          </div>
          <div id="insurance-payment-payer-input">
            <Label>Payer (Optional)</Label>
            <TextField
              type="text"
              placeholder="Enter payer name"
              className="relative rounded-lg text-gray-900"
              label=""
              {...register("insurancePayer")}
            />
          </div>
          <div id="insurance-payment-amount-input">
            <Label>Amount</Label>
            <TextField
              type="number"
              className="relative rounded-lg text-gray-900"
              label=""
              step={0.01}
              addOnLeading={<DollarIcon />}
              {...register("amount", { valueAsNumber: true })}
            />
          </div>
          {(office?.applySurchargeFee || office?.enableDualPricing) && (
            <div id="insurance-payment-surcharge-switch" className="md:col-span-2">
              <Controller
                name="enableSurcharge"
                control={form.control}
                render={({ field: { value, onChange } }) => (
                  <Switch
                    label={`${
                      office.applySurchargeFee ? "Enable Surcharge" : "Enable Dual Pricing"
                    }: Customers pay ${(feeRate * 100).toFixed(2)}% for payments`}
                    checked={value}
                    onCheckedChange={onChange}
                    disabled={
                      (office.applySurchargeFee &&
                        (isACHPayment || (card && card?.cardType?.toLowerCase() !== "credit"))) ||
                      (office.enableDualPricing && isACHPayment)
                    }
                  />
                )}
              />
            </div>
          )}
          <div id="insurance-payment-claim-id-input" className="md:col-span-1">
            <Label>Attach to a Claim (Optional)</Label>
            <TextField
              type="text"
              placeholder="Add Claim ID"
              className="relative rounded-lg text-gray-900"
              label=""
              {...register("claimId", {
                setValueAs: (value) => (value === "" ? undefined : parseInt(value)),
              })}
            />
          </div>
          <div id="insurance-payment-note-textarea" className="md:col-span-1">
            <Label>Note</Label>
            <TextAreaField
              className="rounded-lg text-gray-900"
              placeholder="Note will be included with transaction details."
              rows={3}
              {...register("note")}
              label=""
            />
          </div>

          <div className="border-b border-gray-200" />
          <div className="space-y-2">
            <p className="text-base font-medium text-gray-900">Payment Method</p>
          </div>
          <RadioGroup.Root
            className="space-y-2"
            defaultValue={paymentOption}
            value={paymentOption}
            onValueChange={(value) => {
              setPaymentOption(value);
            }}>
            <div
              id="insurance-payment-method-radio-group-content"
              ref={animationParentRef}
              className="space-y-6">
              <div id="insurance-payment-method-radio-group-content-add-new" className="flex items-center">
                <RadioGroup.Item
                  id="add-new"
                  value="add-new"
                  className="min-w-4 flex h-4 w-4 cursor-pointer items-center rounded-full border border-blue-500 bg-white focus:border-2 focus:outline-none ltr:mr-2 rtl:ml-2">
                  <RadioGroup.Indicator className="relative flex h-4 w-4 items-center justify-center after:block after:h-2 after:w-2 after:rounded-full after:bg-blue-500" />
                </RadioGroup.Item>
                <Label htmlFor="add-new" className="!m-0 flex items-center">
                  Manually enter payment information
                </Label>
              </div>
              {paymentOption === "add-new" && (
                <div id="insurance-payment-method-radio-group-content-add-new-form" className="pl-6">
                  {office?.paymentGateway === PaymentGateway.FINIX && (
                    <PaymentForm
                      appId={appId}
                      env={env}
                      buttonBackColor="#111827"
                      buttonText="Process Insurance Payment"
                      handlePayment={handleFinixPayment}
                      isPaymentProcessing={loading}
                      enableACH={office?.enableACHOnInsurance}
                      setIsACHPayment={setIsACHPayment}
                    />
                  )}
                  {office?.paymentGateway === PaymentGateway.PAYRIX && payrixApiKey && payrixPaymentEnv && (
                    <PayrixForm
                      apiKey={payrixApiKey}
                      environment={payrixPaymentEnv}
                      merchant={office.merchantId || ""}
                      buttonText="Process Insurance Payment"
                      onSuccess={handlePayrixPayment}
                      paymentProcessing={loading}
                      setPaymentError={(err: string) => {
                        showToast(err, "error");
                        setLoading(false);
                      }}
                      enableACH={office.enableACHOnInsurance}
                      setPaymentProcessing={setLoading}
                      setIsACHPayment={setIsACHPayment}
                      disabled={watchAmount === 0}
                    />
                  )}
                </div>
              )}
              {patient && singlePatient && singlePatient.cardList.length > 0 && (
                <div
                  id="insurance-payment-method-radio-group-content-use-existing"
                  className="flex items-center">
                  <RadioGroup.Item
                    id="use-existing"
                    value="use-existing"
                    className="min-w-4 flex h-4 w-4 cursor-pointer items-center rounded-full border border-blue-500 bg-white focus:border-2 focus:outline-none ltr:mr-2 rtl:ml-2">
                    <RadioGroup.Indicator className="relative flex h-4 w-4 items-center justify-center after:block after:h-2 after:w-2 after:rounded-full after:bg-blue-500" />
                  </RadioGroup.Item>
                  <Label htmlFor="use-existing" className="!m-0 flex items-center">
                    Use payment method on file
                  </Label>
                </div>
              )}
              {paymentOption === "use-existing" && patient && (
                <div id="insurance-payment-use-existing-form">
                  <div className="w-full pl-6">
                    <div className="flex w-full gap-2">
                      <PatientStoredPaymentMethodSelect
                        officeId={officeId}
                        patientId={patient}
                        value={card?.id}
                        showDefaultInLabel
                        onCardChange={(c) => {
                          setCard(c);
                          if (c) setValue("cardId", c.id);
                        }}
                      />
                    </div>
                    {formState.errors.cardId && (
                      <div className="text-gray mt-2 flex items-center gap-x-2 text-sm text-red-700">
                        <div>
                          <AlertIcon />
                        </div>
                        <p>Required</p>
                      </div>
                    )}
                  </div>

                  <Button
                    id="insurance-payment-submit-button"
                    className="hover:bg-balck mt-4 flex h-10 w-full justify-center bg-black text-white hover:text-white"
                    type="submit"
                    loadingTextClassName="text-black"
                    loading={loading}>
                    Process Insurance Payment
                  </Button>
                </div>
              )}
            </div>
          </RadioGroup.Root>
        </div>
      </Form>

      <PaymentSuccessDialog
        title="Insurance Payment Successful"
        description="Thanks for your payment, your payment was successful."
        amount={processedAmount}
        account={singlePatient?.firstName || "" + " " + (singlePatient?.lastName || "")}
        date={new Date().toLocaleDateString()}
        source="Insurance Payment"
        open={paymentSuccessDialogOpen}
        handler={() => {
          setPaymentSuccessDialogOpen(false);
          setPatient(undefined);
          reset();
        }}
        onClose={() => {
          setPaymentSuccessDialogOpen(false);
          setPaymentDialog(false);
          setPatient(undefined);
          reset();
        }}
        email={singlePatient?.email || ""}
        phone={singlePatient?.phoneNumbers[0]?.number || ""}
        feeCharged={feeCharged}
      />
    </div>
  );
}
